import { useEffect, useMemo, useState } from 'react'
import { Tabs, Redirect, useRouter } from 'expo-router'
import { Platform, View, Text, TouchableOpacity, Pressable, Image, Modal, StyleSheet, ScrollView, StatusBar, useWindowDimensions } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import Ionicons from '@expo/vector-icons/Ionicons'
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons'
import * as Notifications from 'expo-notifications'
import { useAuth } from '../../lib/AuthContext'
import { fonts, radius, spacing } from '../../lib/theme'
import { useThemeColors } from '../../lib/useThemeColors'
import NotificheDrawer from '../../components/NotificheDrawer'
import { supabase } from '../../lib/supabase'
import { useTranslation } from 'react-i18next'
import ErrorBoundary from '../../components/ErrorBoundary'
import { registerNotificationCategories, handleNotificationAction } from '../../lib/notificationCategories'
import { LOGO } from '../../lib/assets'
import { shouldWarn, confirmLeave } from '../../lib/pickGuard'
import { useNewsUnread } from '../../lib/newsUnread'
import CompetitionSwitcher from '../../components/CompetitionSwitcher'
import NoLeagueState from '../../components/NoLeagueState'


// ─── Componenti Header/Nav (rimasti identici nel codice, ma condizionati nel Layout) ───
function LeagueSwitcher() {
  const { leagueName, userLeagues, switchLeague } = useAuth()
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  if (!userLeagues || userLeagues.length <= 1) {
    return (
      <View style={styles.leagueSingle}>
        <View style={[styles.leagueBar, { backgroundColor: tc.rosa }]} />
        <Text style={styles.leagueSingleText} numberOfLines={1}>{leagueName}</Text>
      </View>
    )
  }
  return (
    <>
      <Pressable
        style={({ pressed }) => [styles.leagueBtn, pressed && { opacity: 0.6 }]}
        onPress={() => setOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={t('nav.active_league')}
        accessibilityState={{ expanded: open }}
      >
        <View style={[styles.leagueBar, { backgroundColor: tc.rosa }]} />
        <Text style={styles.leagueBtnText} numberOfLines={1}>{leagueName}</Text>
        <Text style={styles.leagueArrow}>{open ? '▲' : '▼'}</Text>
      </Pressable>
      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setOpen(false)}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>{t('nav.active_league')}</Text>
            <ScrollView>
              {userLeagues.map(l => (
                <TouchableOpacity
                  key={l.id_league}
                  style={styles.leagueItem}
                  onPress={() => { switchLeague(l.id_league); setOpen(false) }}
                >
                  <View>
                    <Text style={styles.leagueItemName}>{l.name}</Text>
                    <Text style={styles.leagueItemCount}>{t('home.participants', { count: l.member_count })}</Text>
                  </View>
                  {l.is_league_admin && <Text style={styles.adminBadge}>ADMIN</Text>}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  )
}

function PillBellAvatar() {
  const tc = useThemeColors()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const { nickname, user, language } = useAuth()
  const router = useRouter()
  const [unread, setUnread] = useState(0)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const initial = nickname?.[0]?.toUpperCase() ?? '?'
  useEffect(() => {
    if (!user?.id) return
    const loadCount = async () => {
      const { data } = await supabase.rpc('fn_unread_notifications_count', { p_id_user: user.id })
      setUnread(data ?? 0)
    }
    loadCount()
    const interval = setInterval(loadCount, 2 * 60 * 1000)
    return () => clearInterval(interval)
  }, [user?.id])
  return (
    <>
      <View style={styles.pill}>
        <TouchableOpacity
          style={{ position: 'relative', padding: 2 }}
          onPress={() => { setDrawerOpen(true); setUnread(0) }}
        >
          <Ionicons name="notifications-outline" size={18} color={tc.text} />
          {unread > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>{unread > 9 ? '9+' : unread}</Text>
            </View>
          )}
        </TouchableOpacity>
        <TouchableOpacity style={styles.avatar} onPress={() => router.push('/profilo')}>
          <Text style={styles.avatarText}>{initial}</Text>
        </TouchableOpacity>
      </View>
      {drawerOpen && (
        <ErrorBoundary label="NotificheDrawer">
          <NotificheDrawer
            userId={user?.id}
            userLanguage={language}
            onClose={() => setDrawerOpen(false)}
          />
        </ErrorBoundary>
      )}
    </>
  )
}

function AppHeader() {
  const router = useRouter()
  const tc = useThemeColors()
  const { noLeague } = useAuth()
  const styles = useMemo(() => makeStyles(tc), [tc])
  return (
    <View style={styles.headerGrid}>
      <View style={styles.logoRow}>
        <Pressable
          style={({ pressed }) => [styles.logoTap, pressed && { opacity: 0.7 }]}
          onPress={() => router.push('/')}
          accessibilityRole="button"
          accessibilityLabel="Virtua Cycling, torna alla Home"
        >
          <Image
            source={LOGO}
            style={styles.logoImg}
            resizeMode="contain"
          />
          <View style={styles.logoTextWrap}>
            <Text style={styles.logoV}>VIRTUA</Text>
            <Text style={styles.logoC}>CYCLING</Text>
          </View>
        </Pressable>
        <CompetitionSwitcher />
        <View style={{ flex: 1 }} />
        <PillBellAvatar />
      </View>
      {!noLeague && (
        <View style={styles.leagueRow}>
          <LeagueSwitcher />
        </View>
      )}
    </View>
  )
}

function CustomTabBar({ state, descriptors, navigation }) {
  const tc = useThemeColors()
  const { t } = useTranslation()
  const insets = useSafeAreaInsets()
  const styles = useMemo(() => makeStyles(tc), [tc])
  const newsUnread = useNewsUnread()
  const visibleRoutes = state.routes.filter(r => r.name !== 'profilo')
  const iconMap = {
    index: (color) => <Ionicons name="home-outline" size={22} color={color} />,
    pick: (color) => <MaterialCommunityIcons name="bike" size={22} color={color} />,
    leaderboard: (color) => <MaterialCommunityIcons name="trophy-outline" size={22} color={color} />,
    leagues: (color) => <Ionicons name="people-outline" size={22} color={color} />,
    news: (color) => <Ionicons name="newspaper-outline" size={22} color={color} />,
  }
  const labelMap = {
    index: t('nav.home'),
    pick: t('nav.pick'),
    leaderboard: t('nav.leaderboard'),
    leagues: t('nav.leagues'),
    news: 'News',
  }
  return (
    <View style={[styles.tabBar, { backgroundColor: tc.navBg, borderTopColor: tc.navBorder, paddingBottom: insets.bottom }]}>
      {visibleRoutes.map((route) => {
        const isActive = state.routes[state.index]?.name === route.name
        const color = isActive ? tc.rosaText : tc.navIconInactive
        const onPress = () => {
          const event = navigation.emit({ type: 'tabPress', target: route.key, canPreventDefault: true })
          if (!isActive && !event.defaultPrevented) {
            // Lasci la pagina Pick con una selezione diversa da quella salvata: chiedo conferma (una volta per selezione)
            const leavingPick = state.routes[state.index]?.name === 'pick'
            if (leavingPick && shouldWarn()) confirmLeave(t, () => navigation.navigate(route.name))
            else navigation.navigate(route.name)
          }
        }
        return (
          <TouchableOpacity
            key={route.key}
            style={styles.tabItem}
            onPress={onPress}
            activeOpacity={0.7}
          >
            {isActive && <View style={styles.tabIndicator} />}
            <View style={{ position: 'relative' }}>
              {iconMap[route.name]?.(color)}
              {route.name === 'news' && !isActive && newsUnread > 0 && (
                <View
                  style={[styles.badge, { top: -5, right: -9 }]}
                  accessible
                  accessibilityLabel={t('news.tab_unread', { count: newsUnread })}
                >
                  <Text style={styles.badgeText}>{newsUnread > 9 ? '9+' : newsUnread}</Text>
                </View>
              )}
            </View>
            <Text style={[styles.tabLabel, { color, fontWeight: isActive ? '700' : '400' }]}>
              {labelMap[route.name]}
            </Text>
          </TouchableOpacity>
        )
      })}
    </View>
  )
}

// ─── Tab Layout AGGIORNATO (Responsive) ──────────────────────────────────
export default function TabLayout() {
  const { isLoggedIn, loading, switchLeague, noLeague } = useAuth()
  const tc = useThemeColors()
  const { width } = useWindowDimensions()
  const router = useRouter()

  // Rileva se deve nascondere l'interfaccia mobile per mostrare quella desktop del RootLayout
  const isDesktop = Platform.OS === 'web' && width > 1024

  // ─── Registra categories notifica + listener tap bottoni ───────────
  useEffect(() => {
    if (Platform.OS === 'web') return

    // Registra categories (richiede build nativa)
    registerNotificationCategories()

    // Listener tap bottoni / apertura notifica
    const sub = Notifications.addNotificationResponseReceivedListener(response => {
      handleNotificationAction({ response, router, switchLeague })
    })

    return () => sub.remove()
  }, [router, switchLeague])

  if (loading) return null
  if (!isLoggedIn) return <Redirect href="/login" />

  return (
    <View style={{ flex: 1, backgroundColor: tc.bg }}>
      {/* Mostra l'header solo su Mobile/Tablet */}
      {!isDesktop && <AppHeader />}

      {noLeague && (
        <View style={{ flex: 1 }}>
          <NoLeagueState tc={tc} />
        </View>
      )}
      {/* senza lega nella competizione attiva le schermate non vengono montate (leggono tutte leagueId) */}
      {!noLeague && <ErrorBoundary label="app">
        <Tabs
          tabBar={(props) =>
            // Nasconde la barra inferiore su Desktop
            !isDesktop ? <CustomTabBar {...props} /> : null
          }
          screenOptions={{
            headerShown: false,
            // Assicura che lo sfondo sia coerente nel contenitore web del RootLayout
            contentStyle: { backgroundColor: tc.bg },
            animation: 'none'  // ← aggiunge questo

          }}
        >
          <Tabs.Screen name="index" />
          <Tabs.Screen name="pick" />
          <Tabs.Screen name="leaderboard" />
          <Tabs.Screen name="leagues" />
          <Tabs.Screen name="news" />
          <Tabs.Screen name="profilo" options={{ href: null }} />
        </Tabs>
      </ErrorBoundary>}
    </View>
  )
}

const makeStyles = (tc) => StyleSheet.create({
  headerGrid: {
    backgroundColor: tc.navBg,
    paddingTop: Platform.OS === 'ios' ? 50 : (StatusBar.currentHeight ?? 24) + 8,
    borderBottomWidth: 0.5, borderBottomColor: tc.border,
  },
  logoRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 10, paddingTop: 8, paddingBottom: 8, gap: 8,
  },
  logoTap: {
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingRight: 8, marginRight: 2,
    borderRightWidth: 1, borderRightColor: tc.border,
  },
  logoTextWrap: { alignItems: 'flex-start' },
  leagueRow: {
    flexDirection: 'row', alignItems: 'center',
    paddingHorizontal: 12, paddingTop: 2, paddingBottom: 12, gap: 8,
  },
  logoImg: { width: 32, height: 32, borderRadius: 0 },
  logoV: { fontSize: 10.5, fontFamily: fonts.condensedBold, color: tc.text, letterSpacing: 0.3, lineHeight: 12 },
  logoC: { fontSize: 10, fontFamily: fonts.condensed, color: tc.rosaText, letterSpacing: 1.2, lineHeight: 12 },
  // Lega attiva: niente box, come in VB: barretta colore brand + nome (con freccia se ce n'e' piu' di una)
  leagueBar: { width: 3, height: 15, borderRadius: 2 },
  leagueSingle: { flexDirection: 'row', alignItems: 'center', gap: 8, maxWidth: '90%' },
  leagueSingleText: { fontSize: 15, fontFamily: fonts.condensedBold, color: tc.text, letterSpacing: 0.2 },
  leagueBtn: { flexDirection: 'row', alignItems: 'center', gap: 8, maxWidth: '90%' },
  leagueBtnText: { fontSize: 15, fontFamily: fonts.condensedBold, color: tc.text, letterSpacing: 0.2, flexShrink: 1 },
  leagueArrow: { fontSize: 10, color: tc.textDim, marginLeft: 1 },
  bellBtn: { padding: 6, flexShrink: 0 },
  pill: { flexDirection: 'row', alignItems: 'center', backgroundColor: tc.bgCard2, borderWidth: 1, borderColor: tc.border, borderRadius: 24, paddingLeft: 10, paddingRight: 2, paddingVertical: 2, gap: 8, flexShrink: 0 },
  badge: { position: 'absolute', top: -4, right: -4, backgroundColor: tc.rosa, borderRadius: 7, width: 14, height: 14, alignItems: 'center', justifyContent: 'center' },
  badgeText: { fontSize: 8, fontWeight: '800', color: tc.brandText },
  avatar: { width: 32, height: 32, borderRadius: 16, backgroundColor: tc.rosa, alignItems: 'center', justifyContent: 'center' },
  avatarText: { fontSize: 13, fontFamily: fonts.condensedBold, color: tc.brandText },
  modalOverlay: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'flex-start',
    paddingTop: Platform.OS === 'ios' ? 110 : 80,
    paddingHorizontal: 14,
  },
  modalContent: {
    backgroundColor: tc.bgCard2, borderRadius: 12,
    borderWidth: 1, borderColor: tc.border,
    overflow: 'hidden', maxHeight: 300,
  },
  modalTitle: {
    fontSize: 10, fontWeight: '700', color: tc.textDim,
    textTransform: 'uppercase', letterSpacing: 1,
    paddingVertical: 10, paddingHorizontal: 14,
  },
  leagueItem: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingVertical: 12, paddingHorizontal: 14,
    borderTopWidth: 1, borderTopColor: tc.border,
  },
  leagueItemName: { fontSize: 13, fontFamily: fonts.condensedBold, color: tc.text },
  leagueItemCount: { fontSize: 11, color: tc.textDim, marginTop: 2 },
  adminBadge: { fontSize: 10, color: tc.rosaText, fontWeight: '800' },
  tabBar: {
    flexDirection: 'row',
    borderTopWidth: 1,
  },
  tabItem: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 10,
    paddingBottom: 8,
    paddingHorizontal: 2,
    gap: 3,
    position: 'relative',
  },
  tabIndicator: {
    position: 'absolute',
    top: 0,
    width: 24,
    height: 2,
    backgroundColor: tc.rosa,
    borderBottomLeftRadius: 2,
    borderBottomRightRadius: 2,
  },
  tabLabel: {
    fontFamily: fonts.condensedRegular,
    fontSize: 10,
    letterSpacing: 0.2,
    includeFontPadding: false,
  },
})